import { FormEvent, useState } from 'react'
import { router } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { AdminLayout } from '../../../layouts/admin-layout'
import { AppHeader } from '../../../components/app-header'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { AlertCircle, AlertTriangle, Play, Info } from 'lucide-react'

interface TaskConfig {
  description: string
  category: string
  destructive: boolean
  confirmation_message?: string
  required_env_vars: string[]
  parameters: Array<{
    name: string
    type: string
    required: boolean
    description: string
  }>
}

interface EnvCheck {
  satisfied: boolean
  missing: string[]
}

interface AdminRakeTasksNewProps {
  task_name: string
  task_config: TaskConfig
  env_check: EnvCheck
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
  flash?: {
    success?: string
    error?: string
  }
}

function New({ task_name, task_config, env_check }: AdminRakeTasksNewProps) {
  const { t } = useTranslation('admin')
  const [parameters, setParameters] = useState<Record<string, string>>({})
  const [processing, setProcessing] = useState(false)

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()

    if (task_config.destructive && task_config.confirmation_message) {
      if (!confirm(task_config.confirmation_message)) {
        return
      }
    }

    setProcessing(true)
    router.post('/admin/rake_tasks', {
      task_name,
      parameters,
    }, {
      onFinish: () => setProcessing(false),
    })
  }

  const handleParameterChange = (name: string, value: string) => {
    setParameters(prev => ({ ...prev, [name]: value }))
  }

  const canSubmit = env_check.satisfied && !processing

  return (
    <>
      <AppHeader breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: t('rakeTasks.title'), href: '/admin/rake_tasks' },
        { label: t('rakeTasks.runTask') }
      ]} />

      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
            <div className="px-4 lg:px-6 max-w-2xl">
              {/* Page Header */}
              <div className="mb-6">
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-foreground font-mono">{task_name}</h1>
                  {task_config.destructive && (
                    <Badge variant="destructive">{t('rakeTasks.destructive')}</Badge>
                  )}
                </div>
                <p className="mt-2 text-sm text-muted-foreground">
                  {task_config.description}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {t('rakeTasks.category')}: {task_config.category}
                </p>
              </div>

              {/* Environment Variables Warning */}
              {!env_check.satisfied && (
                <Card className="shadow-none mb-6 border-destructive/50 bg-destructive/5">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-destructive flex items-center gap-2 text-base">
                      <AlertCircle className="h-5 w-5" />
                      {t('rakeTasks.missingEnvVars')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-2">
                      {t('rakeTasks.missingEnvVarsDescription')}
                    </p>
                    <ul className="list-disc list-inside space-y-1">
                      {env_check.missing.map(varName => (
                        <li key={varName} className="text-sm font-mono text-destructive">
                          {varName}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Destructive Warning */}
              {task_config.destructive && (
                <Card className="shadow-none mb-6 border-orange-500/50 bg-orange-500/5">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-orange-700 dark:text-orange-400">
                          {t('rakeTasks.destructiveWarning')}
                        </p>
                        {task_config.confirmation_message && (
                          <p className="text-sm text-muted-foreground mt-1">
                            {task_config.confirmation_message}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit}>
                {/* Parameters */}
                {task_config.parameters.length > 0 && (
                  <Card className="shadow-none mb-6">
                    <CardHeader>
                      <CardTitle>{t('rakeTasks.parameters')}</CardTitle>
                      <CardDescription>{t('rakeTasks.parametersDescription')}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {task_config.parameters.map(param => (
                        <div key={param.name} className="space-y-2">
                          <Label htmlFor={param.name}>
                            {param.name}
                            {param.required && <span className="text-red-500 ml-1">*</span>}
                          </Label>
                          <Input
                            id={param.name}
                            type={param.type === 'integer' ? 'number' : 'text'}
                            value={parameters[param.name] || ''}
                            onChange={(e) => handleParameterChange(param.name, e.target.value)}
                            required={param.required}
                            placeholder={param.description}
                            className="font-mono"
                          />
                          <p className="text-xs text-muted-foreground">{param.description}</p>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* No Parameters Info */}
                {task_config.parameters.length === 0 && (
                  <Card className="shadow-none mb-6 bg-muted/50">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <Info className="h-5 w-5" />
                        {t('rakeTasks.noParameters')}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Required Env Vars Info */}
                {task_config.required_env_vars.length > 0 && env_check.satisfied && (
                  <Card className="shadow-none mb-6 bg-green-500/5 border-green-500/20">
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-3">
                        <Info className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm text-muted-foreground">
                            {t('rakeTasks.envVarsSatisfied')}
                          </p>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {task_config.required_env_vars.map(varName => (
                              <Badge key={varName} variant="secondary" className="font-mono text-xs">
                                {varName}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Actions */}
                <div className="flex gap-3 justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => router.visit('/admin/rake_tasks')}
                    disabled={processing}
                  >
                    {t('buttons.cancel', { ns: 'common' })}
                  </Button>
                  <Button
                    type="submit"
                    disabled={!canSubmit}
                    className={task_config.destructive ? 'bg-destructive hover:bg-destructive/90' : ''}
                  >
                    <Play className="h-4 w-4 mr-2" />
                    {processing ? t('rakeTasks.running') : t('rakeTasks.runTask')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

New.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default New
